import { StoreId } from "../decorators/StoreIdDecorator";
import { compareAccDocKey } from "../decorators/StoreIdEquality";
import { AcceleratorBatchSource } from "./AcceleratorBatchSource";
import { AcceleratorDocData, AcceleratorDocStoreKey } from "./AcceleratorDoc";
import {
  AcceleratorDocMetadata,
  AcceleratorDocMetadataData,
} from "./AcceleratorDocMetadata";

export { AcceleratorBatch, AcceleratorBatchData };

interface AcceleratorBatchData {
  uuid: string;
  batchInstanceId: string;
  batchInstanceName: string;
  metadata?: AcceleratorDocMetadataData[];
  source: AcceleratorBatchSource;
  created: number;
  lastModified: number;
}

class AcceleratorBatch {
  @StoreId
  uuid: string;

  created: number;
  lastModified: number;

  batchInstanceId: string;
  batchInstanceName: string;
  source: AcceleratorBatchSource;
  /**
   * Stores our DOCX ids
   */
  metadata: AcceleratorDocMetadata[];

  private _lockedBy?: string;

  get lockedBy() {
    return this._lockedBy;
  }

  get imported() {
    return this.source === "EXTERNAL";
  }

  get stale() {
    return this.metadata.filter((x) => x.stale).length > 0;
  }

  get loadingError() {
    return this.metadata.filter((x) => x.loadingError).length > 0;
  }

  constructor(data: AcceleratorBatchData) {
    this.uuid = data.uuid;
    this.batchInstanceId = data.batchInstanceId;
    this.batchInstanceName = data.batchInstanceName;
    this.source = data.source;
    this.created = data.created;
    this.lastModified = data.lastModified;
    this.metadata = (data.metadata ?? []).map(
      (x) =>
        new AcceleratorDocMetadata({
          ...x,
          batchInstanceId: this.batchInstanceId,
          batchName: this.batchInstanceName,
          source: this.source,
          batchInstanceUuid: this.uuid,
        }),
    );
  }

  lock(lockedBy: string) {
    this._lockedBy = lockedBy;
  }
  unlock() {
    this._lockedBy = undefined;
  }

  setSource(source: AcceleratorBatchSource) {
    this.source = source;
    this.metadata.forEach((x) => (x.source = this.source));
  }

  getMetadata(
    accDocKey: AcceleratorDocStoreKey,
  ): AcceleratorDocMetadata | undefined {
    return this.metadata.find((x) => compareAccDocKey(x.storeId, accDocKey));
  }

  static createTest(
    rest: Partial<AcceleratorBatchData> = {},
  ): AcceleratorBatch {
    const data: AcceleratorBatchData = {
      batchInstanceId: "",
      batchInstanceName: "",
      metadata: [],
      source: "TRANSACT",
      uuid: "",
      created: 0,
      lastModified: 1,
    };

    return new AcceleratorBatch({
      ...data,
      ...rest,
    });
  }

  toData(): AcceleratorBatchData {
    return {
      batchInstanceId: this.batchInstanceId,
      batchInstanceName: this.batchInstanceName,
      created: this.created,
      lastModified: this.lastModified,
      metadata: this.metadata.map((x) => x.toData()),
      source: this.source,
      uuid: this.uuid,
    };
  }
}
